import {useEffect} from 'react';
import {hasModelContext, waitFor, setReactInputValue, setReactSelectValue, safeRegisterTool} from './utils';

/**
 * Registers the `start_free_trial` WebMCP tool when mounted on the /free-trial page.
 * The tool fills and submits both steps of the React-controlled SignupForm on behalf
 * of an agent. Renders nothing — include it anywhere inside the FreeTrialPage tree.
 */
export default function FreeTrialWebMCP() {
  useEffect(() => {
    if (!hasModelContext()) return;

    safeRegisterTool({
      name: 'start_free_trial',
      description:
        'Fill and submit the Workable free trial registration form on /free-trial. Handles both the user step and the account step automatically.',
      inputSchema: {
        type: 'object',
        required: ['firstname', 'lastname', 'email', 'password', 'company_name'],
        properties: {
          firstname: {type: 'string', description: 'First name'},
          lastname: {type: 'string', description: 'Last name'},
          email: {type: 'string', description: 'Work email address'},
          password: {
            type: 'string',
            description: 'Password (min 8 chars, must include uppercase, lowercase, and a number)'
          },
          company_name: {type: 'string', description: 'Company name'},
          website_url: {type: 'string', description: 'Company website URL (optional)'},
          company_size: {
            type: 'string',
            description: 'Company size bracket (optional, defaults to 1_20)',
            enum: ['1_20', '21_50', '51_200', '201_500', '500_']
          },
          phone: {type: 'string', description: 'Phone number (optional)'}
        }
      },
      execute: async ({
        firstname,
        lastname,
        email,
        password,
        company_name: companyName,
        website_url: websiteUrl,
        company_size: companySize = '1_20',
        phone
      }) => {
        // Step 1 — user credentials
        await waitFor('input#userForm_firstname');

        const step1Fields = [
          ['input#userForm_firstname', firstname],
          ['input#userForm_lastname', lastname],
          ['input#userForm_email', email],
          ['input#userForm_password', password]
        ];
        step1Fields.forEach(([selector, value]) => {
          const el = document.querySelector(selector);
          if (!el) throw new Error(`WebMCP: field not found: ${selector}`);
          setReactInputValue(el, value);
        });

        const step1Form = document.querySelector('input#userForm_firstname')?.closest('form');
        const step1Submit =
          step1Form?.querySelector('[type="submit"]') ||
          document.querySelector('button[type="submit"]');
        if (step1Submit) step1Submit.click();

        // Step 2 — account details (appear after step 1 succeeds)
        await waitFor('input#accountForm_name', 30000);

        const nameEl = document.querySelector('input#accountForm_name');
        if (nameEl) setReactInputValue(nameEl, companyName);

        if (websiteUrl) {
          const websiteEl = document.querySelector('input#accountForm_website_url');
          if (websiteEl) setReactInputValue(websiteEl, websiteUrl);
        }

        const sizeEl = document.querySelector('select#accountForm_company_size');
        if (sizeEl) setReactSelectValue(sizeEl, companySize);

        if (phone) {
          const phoneEl = document.querySelector('input#accountForm_phone');
          if (phoneEl) setReactInputValue(phoneEl, phone);
        }

        const step2Form = document.querySelector('input#accountForm_name')?.closest('form');
        const step2Submit = step2Form?.querySelector('[type="submit"]');
        if (step2Submit) step2Submit.click();

        return {
          success: true,
          message: 'Free trial registration submitted. Check your email to confirm your account.'
        };
      }
    });
  }, []);

  return null;
}
